import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI } from './api'

interface NaceItem {
  nace_kode: string
  nace_beskrivelse: string | null
}

interface Props {
  selected: NaceItem[]
  onChange: (items: NaceItem[]) => void
  disabled?: boolean
}

export default function NaceMultiSelect({ selected, onChange, disabled }: Props) {
  const { t } = useTranslation('leads')
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<NaceItem[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // The search runs against a cached code list now, but the field still has to
  // account for a slow first call: with nothing rendered while a request was
  // outstanding, the dropdown read as "no results" rather than "searching",
  // which is how a working filter came to look broken.
  useEffect(() => {
    if (!search || search.length < 1) { setResults([]); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      leadsAPI.searchNaceCodes(search, 20)
        .then(res => {
          if (cancelled) return
          setResults(res.data.filter(r => !selected.some(s => s.nace_kode === r.nace_kode)))
        })
        .catch(() => { if (!cancelled) setResults([]) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(timerRef.current) }
  }, [search, selected])

  function addItem(item: NaceItem) {
    if (!selected.some(s => s.nace_kode === item.nace_kode)) {
      onChange([...selected, item])
    }
    setSearch('')
    setResults([])
  }

  function removeItem(kode: string) {
    onChange(selected.filter(s => s.nace_kode !== kode))
  }

  return (
    <div ref={ref} className="relative">
      {/* The selection is a count inside the field and a list in the dropdown,
          never pills above it: pills grew the column and made the whole filter
          bar jump (#627 AC3). */}
      <input
        type="text"
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        placeholder={t('naceSelect.placeholder')}
        className={`input-field text-sm w-full ${selected.length > 0 ? 'pr-16' : ''}`}
        disabled={disabled}
      />
      {selected.length > 0 && (
        <span
          data-testid="nace-selected-count"
          title={selected.map(s => s.nace_kode).join('\n')}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded-sm bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 text-xs whitespace-nowrap"
        >
          {t('common.nSelected', { n: selected.length })}
        </span>
      )}
      {/* Dropdown — open whenever there is a query, so the field can report
          searching and no-match instead of rendering nothing; and whenever
          something is selected, since that is where the selection is removed */}
      {open && !disabled && (search.length > 0 || selected.length > 0) && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          {selected.length > 0 && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              {selected.map(item => (
                <div key={item.nace_kode} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  <span className="font-mono text-xs text-primary-600 dark:text-primary-400 whitespace-nowrap">{item.nace_kode}</span>
                  <span className="flex-1 truncate text-gray-700 dark:text-gray-300">{item.nace_beskrivelse ?? ''}</span>
                  <button
                    onClick={() => removeItem(item.nace_kode)}
                    aria-label={t('naceSelect.remove', { kode: item.nace_kode })}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {search.length > 0 && loading && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('common.searching')}
            </div>
          )}
          {search.length > 0 && !loading && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {t('naceSelect.noMatch')}
            </div>
          )}
          {search.length > 0 && !loading && results.map(r => (
            <button
              key={r.nace_kode}
              onClick={() => addItem(r)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex gap-2"
            >
              <span className="font-mono text-xs text-primary-600 dark:text-primary-400 whitespace-nowrap">{r.nace_kode}</span>
              <span className="truncate text-gray-700 dark:text-gray-300">{r.nace_beskrivelse ?? ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
